import React from 'react';
import { always, times, isNil, pathOr, prop } from 'ramda';
import { scaleLinear, scaleBand, scalePoint } from '@vx/scale';
import { Group } from '@vx/group';
import { ParentSize } from '@vx/responsive';
import { BoxPlot } from '@vx/stats';
import { useTooltipInPortal } from '@vx/tooltip';
import { useIntl } from 'gatsby-plugin-intl';
import { measureNoWrappedText } from '../../../utils/measureText';
import { BOX_PLOT_DEFAULT } from '../configs/boxPlot.config';
import { useChartTooltipContext } from '../../../store/ChartTooltipContext';
import { useGatsbyContext } from '../../../store/GatsbyContext';
import { renderOpenTooltip } from '../tooltipBasic/tooltipBasic.utils';
import { TooltipWithPortal } from '../tooltipWithPortal/tooltipWithPortal.component';
import { Header } from '../header';
import { isNoData, NoData } from '../noData';
import { Container, Chart, Svg } from '../charts.styles';
import { DEFAULT_SIZE } from '../charts.constants';
import { BoxGroup, Label, ValueLabel, Line, labelStyle } from './boxPlotChart.styles';
import { TooltipElementTable, TooltipTableDataCell } from '../../tooltipElementTable/tooltipElementTable.styles';
import { toFixed } from '../../../utils/math';
import { TOOLTIP_MIN_WIDTH } from '../../tooltipElementTable/tooltipElementTable.constants';
import { CHART_PALETTE } from '../../../utils/colors';
import { Circle } from '@vx/shape';

const getBarColor = (colorFromConfig, index) => {
  const colorFromPalette = CHART_PALETTE[2];
  const secondaryColor = colorFromPalette === colorFromConfig ? CHART_PALETTE[0] : colorFromPalette;
  const isNumberEven = index % 2 === 0;

  return isNumberEven ? secondaryColor : colorFromConfig;
};

export const BoxPlotChart = ({
  width = DEFAULT_SIZE,
  data: chartData = {},
  tabs,
  parser: customParser,
  config: customConfig = {},
}) => {
  if (customParser) customParser(chartData);

  const tooltip = useChartTooltipContext();
  const intl = useIntl();
  chartData['title'] = intl.formatMessage({ id: chartData.title });
  const { title, subtitle, series } = chartData;
  const data = chartData.transformedData || chartData.data;

  if (isNoData(data, series)) return <NoData width={width} height={height} />;

  const config = {
    ...BOX_PLOT_DEFAULT,
    ...customConfig,
  };

  const {
    rowHeight,
    valueAxsisSpace,
    minValue,
    maxValue,
    color,
    padding,
    labelAxisMargin,
    valueAxisStep,
    rightMargin,
  } = config;
  const { containerRef } = useTooltipInPortal();

  const height = data.length * rowHeight + 2 * valueAxsisSpace;
  const labelAxisSpace = Math.max(...data.map(({ id }) => measureNoWrappedText(id, labelStyle).width));

  const xLabels = times((index) => (minValue + index * valueAxisStep).toFixed(1), (maxValue - minValue) * 2 + 1);

  const handleHover = (index) => (e) => {
    const item = e.target.getBoundingClientRect();
    const top = item.top;
    const left = item.left + TOOLTIP_MIN_WIDTH;

    if (left === tooltip.left && top === tooltip.top) {
      return;
    }

    tooltip.show({
      data: {
        index,
      },
      top: top,
      left: left,
    });
  };

  const renderTooltipContent = () => {
    const VALUE_COLUMN_NAME = 'Range';
    const VALUE2_COLUMN_NAME = 'Median';

    const CATEGORY_COLUMN_NAME = 'Assessment';
    const { index } = tooltip.data || {};

    const { piLabels } = useGatsbyContext();
    const { formatMessage } = useIntl();
    return (
      !isNil(index) && (
        <TooltipElementTable>
          <thead>
            <tr>
              <th>{formatMessage({ id: CATEGORY_COLUMN_NAME })}</th>
              <th style={{ textAlign: 'center', paddingRight: '10px' }}>{formatMessage({ id: VALUE_COLUMN_NAME })}</th>
              <th style={{ textAlign: 'center' }}>{formatMessage({ id: VALUE2_COLUMN_NAME })}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <TooltipTableDataCell>
                {formatMessage({ id: piLabels[index].indicator })}: {formatMessage({ id: piLabels[index].label })}
              </TooltipTableDataCell>
              <TooltipTableDataCell color={getBarColor(color, index)} style={{ paddingRight: '10px' }}>
                <span style={{ minWidth: '40px' }}>
                  {toFixed(data[index].q1, 1)} - {toFixed(data[index].q3, 1)}
                </span>
              </TooltipTableDataCell>
              <TooltipTableDataCell color={getBarColor(color, index + 1)}>
                <span style={{ minWidth: '40px' }}>{data[index].median}</span>
              </TooltipTableDataCell>
            </tr>
          </tbody>
        </TooltipElementTable>
      )
    );
  };

  const hoveredGroupIndex = pathOr(null, ['data', 'index'], tooltip);
  const isHoverActive = !isNil(hoveredGroupIndex);

  return (
    <Container width={width}>
      <Header title={title} subtitle={subtitle} tabs={tabs} config={config}></Header>
      <Chart width={width} height={height} ref={containerRef}>
        {renderOpenTooltip(always(<TooltipWithPortal>{renderTooltipContent()}</TooltipWithPortal>), tooltip)}
        <ParentSize>
          {({ width: chartWidth, height: chartHeight }) => {
            const yStart = valueAxsisSpace;
            const yEnd = chartHeight - valueAxsisSpace;

            const yScale = scaleBand({
              domain: data.map(prop('id')),
              range: [yStart, yEnd],
              padding,
            });

            const barHeight = yScale.bandwidth();

            const xScale = scaleLinear({
              domain: [minValue, maxValue],
              range: [labelAxisSpace + labelAxisMargin, chartWidth - rightMargin],
            });

            const xLabelScale = scalePoint({
              domain: xLabels,
              range: xScale.range(),
            });

            const yStartLabel = yStart - 10;
            const yEndLabel = yEnd + 14;

            const circleR = 5;
            const circleY = barHeight / 2;

            return (
              <Svg>
                <Group>
                  {xLabels.map((label, key) => (
                    <Group transform={`translate(${xLabelScale(label)}, 0)`} key={key}>
                      <ValueLabel y={yStartLabel}>{label}</ValueLabel>
                      <Line y1={yStart} y2={yEnd} />
                      <ValueLabel y={yEndLabel}>{label}</ValueLabel>
                    </Group>
                  ))}
                </Group>
                <Group>
                  {data.map(({ id, min, max, q1, q3, median }, key) => (
                    <Group top={yScale(id)} left={0} key={key}>
                      <Label transform={`translate(${labelAxisSpace}, ${labelStyle.fontSize})`}>{id}</Label>
                      <BoxGroup
                        isHoverActive={isHoverActive}
                        isGroupHovered={hoveredGroupIndex === key}
                        {...tooltip.bindHoverEvents(handleHover(key))}
                      >
                        <BoxPlot
                          min={min}
                          max={max}
                          top={0}
                          firstQuartile={q1}
                          thirdQuartile={q3}
                          median={median}
                          medianProps={{
                            opacity: 0,
                          }}
                          boxWidth={barHeight}
                          fill={getBarColor(color, key)}
                          stroke={getBarColor(color, key)}
                          strokeWidth={1}
                          valueScale={xScale}
                          horizontal="true"
                          rx={0}
                          ry={0}
                        />
                        <Circle
                          className="dot"
                          cy={circleY}
                          cx={xScale(median)}
                          r={circleR}
                          fill={getBarColor(color, key + 1)}
                        />
                      </BoxGroup>
                    </Group>
                  ))}
                </Group>
              </Svg>
            );
          }}
        </ParentSize>
      </Chart>
    </Container>
  );
};
